// ViewMachines.jsx
import {FaCheckCircle, FaTimesCircle} from 'react-icons/fa';
import ReFetchIcon from "../../../media/re-fetch.svg";
import DeleteItemIcon from "../../../media/delete-item.svg";
import EditItemIcon from "../../../media/edit-item.svg";
import EditRulesIcon from "../../../media/edit-item-rules.svg";
import "../../../css/machine-view-list.css";

import {useState} from "react";
import {useTranslation} from "react-i18next";
import moment from 'moment';

export const ViewMachines = ({
                                 data,
                                 editHandler,
                                 deleteHandler,
                                 isLoadingDelete,
                                 getInventoryInfoById,
                                 setErrorMessage
                             }) => {
    const {t} = useTranslation();

    const [inventoryInfoValues, setInventoryInfoValues] = useState({});
    const [inventoryLoading, setInventoryLoading] = useState({});
    const [detailsOpen, setDetailsOpen] = useState(false);
    const formatEUR = (value) =>
        new Intl.NumberFormat('el-GR', {style: 'currency', currency: 'EUR'})
            .format(Number(value || 0));

    const extractErrMsg = (err) => (
        err?.response?.data?.error ||
        err?.error ||
        err?.message ||
        'Failed to fetch inventory info'
    );

    const handleFetchInventoryInfo = async (machineId) => {
        if (inventoryLoading[machineId]) return;

        try {
            setInventoryLoading((p) => ({...p, [machineId]: true}));
            const response = await getInventoryInfoById(machineId);

            setInventoryInfoValues((prev) => ({
                ...prev,
                [machineId]: {
                    totalCurrentStock: response?.totalCurrentStock ?? 0,
                    totalCurrentPrice: response?.totalCurrentPrice ?? 0,
                },
            }));
        } catch (err) {
            const msg = extractErrMsg(err);
            console.error('getInventoryInfoById failed:', err);
            if (setErrorMessage) setErrorMessage(msg);

            setInventoryInfoValues((prev) => ({
                ...prev,
                [machineId]: {
                    error: msg,
                    totalCurrentStock: null,
                    totalCurrentPrice: null,
                },
            }));
        } finally {
            setInventoryLoading((p) => ({...p, [machineId]: false}));
        }
    };

    // ---------------- Activity helpers ----------------
    const parseTs = (ts) => {
        if (!ts) return null;
        const m = moment(ts);
        return m.isValid() ? m : null;
    };

    const minutesAgo = (ts) => {
        const m = parseTs(ts);
        if (!m) return Infinity;
        return moment().diff(m, 'minutes');
    };

    const timeAgoShort = (ts) => {
        const m = parseTs(ts);
        if (!m) return "—";
        const mins = moment().diff(m, 'minutes');
        if (mins < 1) return "now";
        if (mins < 60) return `${mins}m`;
        const hrs = moment().diff(m, 'hours');
        if (hrs < 24) return `${hrs}h`;
        const days = moment().diff(m, 'days');
        return `${days}d`;
    };

    // Per-timestamp level (this is what you want in hover)
    const getTsLevel = (ts) => {
        const mins = minutesAgo(ts);
        if (mins >= 1440) return "danger";
        if (mins > 5) return "warning";
        return "success";
    };

    // Row health = worst of both timestamps (pill color)
    const getHealthLevel = (machine) => {
        const mm = minutesAgo(machine?.last_machine_connection);
        const km = minutesAgo(machine?.last_kiosk_activity);
        // Terminal machines have no kiosk of their own: only the MAGEX server
        // connection decides health. Ignore the kiosk timestamp for them so a
        // down kiosk never turns the row red/yellow.
        const worst = machine?.isTerminal ? mm : Math.max(mm, km);

        if (worst >= 1440) return "danger";
        if (worst > 5) return "warning";
        return "success";
    };

    //  Custom hover tooltip with per-line colors
    const StatusCombinedCell = ({machine}) => {
        const status = machine?.machine_status || "—";
        const hasStocks = Array.isArray(machine?.stocks) && machine.stocks.length > 0;
        // Coffee machines are not part of the normal kiosk/MAGEX health monitoring,
        // so show them as a neutral gray pill instead of green.
        const coffee = !!machine?.isCoffee;
        const rowHealth = coffee ? "neutral" : (hasStocks ? getHealthLevel(machine) : "success");
        const pillCls =
            rowHealth === "danger" ? "status-danger" :
                rowHealth === "warning" ? "status-warning" :
                    rowHealth === "neutral" ? "status-neutral" :
                        "status-success";

        const magexTs = machine?.last_machine_connection; // MAGEX Activity (your old column)
        const kioskTs = machine?.last_kiosk_activity;      // Kiosk Activity (your old column)

        const showInline = !coffee && hasStocks && rowHealth !== "success";
        const inlineLine = (label, ts, level) => {
            const m = parseTs(ts);
            const title = m ? m.format('MMM D, h:mm A') : "—";
            return (
                <div className={`status-inline-row-warn ttp-${level}`} title={`${label}: ${title}`}>
                    <span className="status-inline-warn-label">{label}:</span>
                    <span className="status-inline-warn-ago">{timeAgoShort(ts)}</span>
                </div>
            );
        };

        const terminal = !!machine?.isTerminal;
        const magexLevel = getTsLevel(magexTs);
        // For terminal machines the kiosk connection is irrelevant, so show it
        // as a neutral (gray) line instead of a red/yellow warning.
        const kioskLevel = terminal ? "neutral" : getTsLevel(kioskTs);

        const magexMoment = parseTs(magexTs);
        const kioskMoment = parseTs(kioskTs);

        const magexText = magexMoment ? magexMoment.format('MMM D, h:mm A') : "—";
        const kioskText = kioskMoment ? kioskMoment.format('MMM D, h:mm A') : "—";

        return (
            <span className="status-hover-wrap">
                        <span className={`status-pill ${pillCls}`}>
                          {status}
                        </span>

                {/* Inline block under pill ONLY if warning/danger */}
                {showInline ? (
                    <div className="status-inline">
                        {inlineLine("MAGEX", magexTs, magexLevel)}
                        {terminal ? null : inlineLine("Kiosk", kioskTs, kioskLevel)}
                    </div>
                ) : null}

                {/* Keep hover tooltip (optional, still useful) */}
                <div className="status-tooltip">
                      <div className={`status-tooltip-row tt-${magexLevel}`}>
                        <span className="tt-label">MAGEX</span>
                        <span className="tt-value">{magexText}</span>
                        <span className="tt-ago">({timeAgoShort(magexTs)})</span>
                      </div>

                      <div className={`status-tooltip-row tt-${kioskLevel}`}>
                        <span className="tt-label">Kiosk</span>
                        <span className="tt-value">{kioskText}</span>
                        <span className="tt-ago">({timeAgoShort(kioskTs)})</span>
                      </div>
                    </div>
                  </span>
        );
    };

    // ---------------- Details helpers ----------------
    const boolBadge = (ok, yesLabel = "ON", noLabel = "OFF") => (
        <span className={`badge ${ok ? "bg-success" : "bg-secondary"}`}>
      {ok ? yesLabel : noLabel}
    </span>
    );

    const tempClass = (t) => {
        if (t === null || t === undefined || Number.isNaN(Number(t))) return "";
        const temp = Number(t);

        if (temp <= -20) return "temp-deep-blue";
        if (temp <= 0) return "temp-blue";
        if (temp <= 5) return "text-success";
        if (temp <= 10) return "text-warning";
        return "text-danger";
    };

    const StockStatusCell = ({stocks, machine, orderId, forceOpen}) => {
        const hasStocks = Array.isArray(stocks) && stocks.length > 0;

        const TEMP_DELTA_THRESHOLD = 12;

        // ---------- helpers ----------
        const toFinite = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? n : null;
        };

        const getDelta = (s) => {
            const t = toFinite(s.temperature);
            const ev = toFinite(s.temperature_evap);
            if (t === null || ev === null) return null;
            return t - ev;
        };

        // ---------- temp delta logic ----------
        const deltas = hasStocks
            ? stocks
                .map(getDelta)
                .filter((d) => d !== null)
            : [];

        const maxDelta = deltas.length ? Math.max(...deltas) : null;

        const hasTempProblem =
            maxDelta !== null && maxDelta > TEMP_DELTA_THRESHOLD;

        // ---------- door status ----------
        const anyDoorOn = hasStocks ? stocks.some(s => !!s.door_open) : false;

        // ---------- chips ----------
        const chips = [];

        if (!hasStocks) {
            chips.push({key: "ok", label: "OK", cls: "mv-chip mv-chip-ok"});
        } else {

            if (hasTempProblem) {
                chips.push({
                    key: "temp",
                    label: "TEMP",
                    cls: "mv-chip mv-chip-temp-warn",
                    title: `Δ(T-Ev): ${maxDelta.toFixed(1)}°C (threshold ${TEMP_DELTA_THRESHOLD}°C)`
                });
            }

            if (anyDoorOn) {
                chips.push({
                    key: "door",
                    label: "DOOR ON",
                    cls: "mv-chip mv-chip-door",
                    title: "One or more doors are open"
                });
            }

            if (chips.length === 0) {
                chips.push({key: "ok", label: "OK", cls: "mv-chip mv-chip-ok"});
            }
        }

        return (
            <details className="stock-details" open={!!forceOpen}>
                <summary className="stock-summary">

                    <div className="mv-chip-row">
                        {chips.map(c => (
                            <span
                                key={c.key}
                                className={c.cls}
                                title={c.title || ""}
                            >
                            {c.label}
                        </span>
                        ))}
                    </div>

                    <span className="ms-2 small text-muted">
                    {hasStocks
                        ? `${stocks.length} stock${stocks.length > 1 ? "s" : ""}`
                        : "no stock"} •
                </span>

                </summary>

                <div className="machine-details-meta small text-muted mt-2">
                    <div>
                        <b>Order Processing:</b> {orderId || "—"}
                    </div>
                    <div>
                        <b>GUI:</b> {machine?.gui_version ?? "—"} &nbsp;
                        <b>Master:</b> {machine?.master_version ?? "—"}
                    </div>

                    <div>
                        <b>Credit:</b> {machine?.credit ?? 0} &nbsp;
                        <b>Max:</b> {machine?.max_change ?? 0} &nbsp;
                        <b>Coins:</b> {machine?.coins_total_stock ?? "—"}
                    </div>
                </div>

                {!hasStocks ? (
                    <div className="text-muted mt-2">—</div>
                ) : (
                    <div className="stock-list mt-2">
                        {stocks.map((s, idx) => {

                            const t = Number(s.temperature);
                            const ev = Number(s.temperature_evap);
                            const delta =
                                Number.isFinite(t) && Number.isFinite(ev)
                                    ? (t - ev).toFixed(1)
                                    : "—";

                            return (
                                <div key={idx} className="stock-row">

                                    <div className="stock-row-top">

                                    <span className="stock-id">
                                        #{s.stock}
                                    </span>

                                        <span className={`stock-temp ${tempClass(s.temperature)}`}>
                                        T: {Number(s.temperature).toFixed(1)}°C
                                    </span>

                                        <span className={`stock-evap ${tempClass(s.temperature_evap)}`}>
                                        Ev: {Number(s.temperature_evap).toFixed(1)}°C
                                    </span>

                                        <span className="stock-delta text-muted">
                                        Δ: {delta}°
                                    </span>

                                    </div>

                                    <div className="stock-row-badges">

                                    <span className={`badge ${s.compressor ? "bg-primary" : "bg-success"}`}>
                                        COMP
                                    </span>

                                        {boolBadge(!s.nofrost, "NOFROST", "NOFROST")}
                                        {boolBadge(!s.door_open, "DOOR OK", "DOOR OPEN")}

                                        {s.pickup_to_close ? (
                                            <span className="badge bg-warning text-dark">
                                            PICKUP
                                        </span>
                                        ) : null}

                                    </div>

                                </div>
                            );
                        })}
                    </div>
                )}
            </details>
        );
    };
    const openRulesTab = (machine_id) => {
        const url = `/admin/auth/machine-price-rules?machine_id=${machine_id}`;
        window.open(url, '_blank');
    };

    return (
        <div className='table-responsive bg-light p-3 mt-2'>
            <div className="d-flex justify-content-end mb-2">
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setDetailsOpen(v => !v)}
                >
                    {detailsOpen ? "Collapse details" : "Expand details"}
                </button>
            </div>
            <table className='table table-sm table-border edit-grid-view'>
                <thead className='border-0'>
                <tr>
                    <th>Enabled</th>
                    <th>Kiosk External ID</th>
                    <th>Friendly Name</th>
                    <th>Status</th>
                    <th>{t("Inventory Info")}</th>
                    <th>Details</th>
                    <th>{t("Actions")}</th>
                </tr>
                </thead>

                <tbody>
                {data && data.data ? (
                    data.data.map((machine) => {
                        const info = inventoryInfoValues[machine._id];

                        return (
                            <tr key={machine._id}>
                                <td>
                                    {machine.confirmed ? (
                                        <FaCheckCircle className='text-success'/>
                                    ) : (
                                        <FaTimesCircle className='text-danger'/>
                                    )}
                                </td>

                                <td>{machine.kiosk_external_id}</td>
                                <td>{machine.machine_name}</td>

                                {/*  Single status column with colored hover rows */}
                                <td className="machine-status-td">
                                    <StatusCombinedCell machine={machine}/>
                                </td>

                                <td
                                    onClick={() => handleFetchInventoryInfo(machine._id)}
                                    style={{cursor: 'pointer'}}
                                >
                                    {inventoryLoading[machine._id] ? (
                                        <div className="fetch-inventory-info">
                                            <span className="spinner-border spinner-border-sm"/>
                                        </div>
                                    ) : inventoryInfoValues[machine._id]?.error ? (
                                        <div className="text-danger small">
                                            {inventoryInfoValues[machine._id].error}
                                        </div>
                                    ) : info ? (
                                        <div className="inventory-info-cell">
                                            <div className="inventory-stock-line">{info.totalCurrentStock}</div>
                                            <div
                                                className="inventory-price-line">{formatEUR(info.totalCurrentPrice)}</div>
                                        </div>
                                    ) : (
                                        <div className="fetch-inventory-info">
                                            <img className="refetch-icon" src={ReFetchIcon} alt="Fetch Icon"/>
                                        </div>
                                    )}
                                </td>
                                <td>
                                    <StockStatusCell
                                        stocks={machine.stocks}
                                        machine={machine}
                                        orderId={machine.machine_order_id_processing}
                                        forceOpen={detailsOpen}
                                    />
                                </td>
                                <td>
                                    <div className='btn-group'>
                                        <button
                                            className="btn edit-rules-icon"
                                            onClick={() => openRulesTab(machine.kiosk_external_id)}
                                        >
                                            <img
                                                src={EditRulesIcon}
                                                alt="Edit Rules"
                                                className="icon-img action-icons-img"
                                            />
                                        </button>

                                        <button
                                            className="btn edit-item-icon"
                                            onClick={() => editHandler(machine)}
                                            data-bs-toggle="modal"
                                            data-bs-target="#machineModal"
                                        >
                                            <img
                                                src={EditItemIcon}
                                                alt="Edit"
                                                className="icon-img action-icons-img"
                                            />
                                        </button>

                                        <button
                                            className="btn delete-item-icon"
                                            onClick={() => deleteHandler(machine._id)}
                                            disabled={isLoadingDelete}
                                        >
                                            {isLoadingDelete ? (
                                                <span className="spinner-border spinner-border-sm"/>
                                            ) : (
                                                <img
                                                    src={DeleteItemIcon}
                                                    alt="Delete"
                                                    className="icon-img action-icons-img"
                                                />
                                            )}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })
                ) : (
                    <tr>
                        <td colSpan="9">Error: Failed to fetch machine data</td>
                    </tr>
                )}
                </tbody>
            </table>
        </div>
    );
};

export default ViewMachines;